import React, { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import Layout from '../../components/layout'
import { useLocation, useParams } from 'react-router-dom'
import Loading from '_components/loading'
import { GET_ACTIVITY, SAVE_ACTIVITY } from './gql'
import { getFormDataFields } from './helpers'
import ScrollToTop from '_components/scroll-to-top'

import GeneralInformation from './components/general-information'
import AdditionalInformation from './components/additional-information'

import { MbToB } from 'services/file'
import { useUser } from '_security/session-hooks'
import { useAppConfig, useAppLabel } from '_application'
import { useIntl } from 'react-intl'

export const ActivityForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    coverPhoto: null,
    description: '',
    body: '',
    private: false,
    publishedAt: null,
    taxonomy: [],
    location: {
      city: '',
      country: '',
      label: '',
      lat: '',
      long: '',
    },
  })

  const { id } = useParams()
  const { pathname } = useLocation()
  const { sub } = useUser()
  const { tenantId } = useAppConfig()

  const intl = useIntl()
  const appLabel = useAppLabel()

  const [saveActivity] = useMutation(SAVE_ACTIVITY)

  const { loading, error, data } = useQuery(GET_ACTIVITY, {
    variables: {
      id,
    },
    skip: !id,
  })

  const entity = data?.getActivity

  const onSubmit = () => {
    try {
      saveActivity({
        variables: {
          input: formData,
        },
        update: (cache) => {
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'getAllActivitiesPublic' })
          cache.evict({ id: `Activity:{"id":"${id}"}` })
        },
      })
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const changeFormProperty = (change) => {
    setFormData((prev) => {
      return {
        ...prev,
        ...change,
      }
    })
  }

  useMemo(async () => {
    if (!id) {
      const savedActivity = await saveActivity()
      setFormData({ ...formData, id: savedActivity?.data?.saveActivity?.id })
    }
    if (data) setFormData(getFormDataFields(data.getActivity))
  }, [data])

  if (!formData?.id || loading) return <Loading /> // todo spinner comp
  if (error) return `Error! ${error}`

  const tagsConfig = {
    userId: sub,
    tenantId: tenantId,
    context: 'content-studio-activity-banner-image',
    mediaId: 'activityID',
  }

  const uploadConfig = {
    folderName: 'content-studio',
    tagsConfig,
    maxImageFileSize: MbToB(1),
    maxImageWidth: 2000,
    allowedFormats: ['jpg', 'png', 'svg', 'webp'],
  }

  const resolvedTitle =
    pathname?.split('/')?.pop()?.toLowerCase() === 'edit' ? 'edit' : 'new'

  const resolvedEntityName = appLabel('activities', 'activities.title')

  const title = intl.formatMessage(
    {
      id: 'contentstudiot.pagetitle',
      defaultMessage: `${
        resolvedTitle.charAt(0).toUpperCase() + resolvedTitle.slice(1)
      } ${resolvedEntityName.toLowerCase()}`,
    },
    {
      entity: resolvedEntityName.toLowerCase(),
    }
  )

  const isSubmitDisabled =
    formData?.name.length === 0 ||
    formData?.description.length === 0 ||
    formData?.body === '<p><br></p>' ||
    !formData?.coverPhoto

    
  return (
    <Layout onSubmit={onSubmit} isSubmitDisabled={isSubmitDisabled}>
      <ScrollToTop />
      <h1>{title}</h1>
      <GeneralInformation
        formData={formData}
        changeFormProperty={changeFormProperty}
        uploadConfig={uploadConfig}
        entity={entity}
      />
      <AdditionalInformation
        formData={formData}
        changeFormProperty={changeFormProperty}
        uploadConfig={uploadConfig}
        entity={entity}
      />
    </Layout>
  )
}

export default ActivityForm

